import { c as _c } from "react/compiler-runtime";
import Head from 'next/head';
import { useRef } from 'react';
import Button from '@helios/Button';
import Container from '@helios/Container';
import PageHero from '@helios/PageHero';
import Section from '@helios/Section';
import UniversityBadge from '@helios/UniversityBadge';
import Layout from '@/components/Layout';
import CTA from '@/components/CTA.js';
import ApplyNow from '@/components/ApplyNow.js';
import InterestIcon, { INTERESTS } from '@/components/InterestIcon.js';
import { SiteFAQSection } from '@/components/SiteFAQAccordion.js';
import { getAllProfessors, getProfessorBySlug } from '@/utils/api.js';
import useScroll from '@/hooks/useScroll.js';
import { allSites } from '@/utils/domains.js';
import BrandsInstagramIcon from '@/components/icons/brands/instagram';
import BrandsTwitterIcon from '@/components/icons/brands/twitter';
import LightGraduationCapIcon from '@/components/icons/light/graduation-cap';
import ThinArrowDownLongIcon from '@/components/icons/thin/arrow-down-long';
import styles from '@/styles/ProfessorDetailPage.module.scss';

// The cover fills the hero's media column: the full width below 992px, half
// the page from 992px.
const COVER_IMAGE_SIZES = '(max-width: 991px) 100vw, 50vw';
export async function getStaticPaths() {
  const professors = getAllProfessors();
  return {
    paths: allSites.reduce((acc, site) => {
      return acc.concat(professors.filter(p => !p.canonicalPath).map(p => {
        return {
          params: {
            site,
            slug: p.slug
          }
        };
      }));
    }, []),
    fallback: false
  };
}
export async function getStaticProps({
  params
}) {
  const professor = getProfessorBySlug(params.slug);
  return {
    props: {
      professor,
      site: params.site
    }
  };
}
function Professor(t0) {
  const $ = _c(113);
  const {
    professor,
    site
  } = t0;
  const {
    scrollTo
  } = useScroll();
  const bioRef = useRef(null);
  const showTeaches = !professor.affiliationDetail && !professor.featuredDegree && !!(professor.teaches || professor.alsoTeachesAt);
  let t1;
  if ($[0] !== professor.doctorate || $[1] !== professor.name) {
    t1 = professor.doctorate ? <title>{`${professor.name}, ${professor.doctorate} | Campus.edu`}</title> : <title>{`${professor.name} | Campus.edu`}</title>;
    $[0] = professor.doctorate;
    $[1] = professor.name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = `https://campus.edu${professor.canonicalPath ?? `/professors/${professor.slug}`}`;
  let t3;
  if ($[3] !== t2) {
    t3 = <link rel="canonical" href={t2} />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <meta httpEquiv="X-UA-Compatible" content="IE=Edge" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== professor.description) {
    t5 = <meta name="description" content={professor.description} />;
    $[6] = professor.description;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <meta name="viewport" content="width=device-width, initial-scale=1" />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t1 || $[10] !== t3 || $[11] !== t5) {
    t7 = <Head>{t1}{t3}{t4}{t5}{t6}</Head>;
    $[9] = t1;
    $[10] = t3;
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = `/images/professors/${professor.images.cover}`;
  let t9;
  if ($[13] !== professor.images.alt || $[14] !== t8) {
    t9 = <PageHero.Image alt={professor.images.alt} frame="portrait" priority={true} sizes={COVER_IMAGE_SIZES} src={t8} />;
    $[13] = professor.images.alt;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== professor.affiliationDetail) {
    t10 = !!professor.affiliationDetail && <small>{professor.affiliationDetail}</small>;
    $[16] = professor.affiliationDetail;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== professor.featuredDegree) {
    t11 = !!professor.featuredDegree && <small>{professor.featuredDegree} from:</small>;
    $[18] = professor.featuredDegree;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== professor.teaches || $[21] !== showTeaches) {
    t12 = showTeaches && <small>Also teaches{professor.teaches ? ` ${professor.teaches}` : ""} at:</small>;
    $[20] = professor.teaches;
    $[21] = showTeaches;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== professor.taught) {
    t13 = !!professor.taught && <small>Also taught {professor.taught} at:</small>;
    $[23] = professor.taught;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== professor.name || $[26] !== t10 || $[27] !== t11 || $[28] !== t12 || $[29] !== t13) {
    t14 = <p>{professor.name}{t10}{t11}{t12}{t13}</p>;
    $[25] = professor.name;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== professor.university) {
    t15 = <div className={styles["caption-badge"]}><UniversityBadge name={professor.university} /></div>;
    $[31] = professor.university;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== professor.hideSocial) {
    t16 = !professor.hideSocial && <ul className={styles.social}><li><a className={styles["social-link"]} href="#" target="_blank" rel="noopener noreferrer"><BrandsInstagramIcon /></a></li><li><a className={styles["social-link"]} href="#" target="_blank" rel="noopener noreferrer"><BrandsTwitterIcon /></a></li></ul>;
    $[33] = professor.hideSocial;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t14 || $[36] !== t15 || $[37] !== t16) {
    t17 = <PageHero.Caption>{t14}{t15}{t16}</PageHero.Caption>;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t17 || $[40] !== t9) {
    t18 = <div className={styles["hero-media-cell"]}><PageHero.Media placement="split">{t9}{t17}</PageHero.Media></div>;
    $[39] = t17;
    $[40] = t9;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== professor.name) {
    t19 = <h1 className={styles.title}>{professor.name}</h1>;
    $[42] = professor.name;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== professor.affiliationDetail || $[45] !== professor.university) {
    t20 = !!professor.affiliationDetail && <p className={styles.affiliation}>{professor.affiliationDetail}{" "}<UniversityBadge name={professor.university} /></p>;
    $[44] = professor.affiliationDetail;
    $[45] = professor.university;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== professor.featuredDegree || $[48] !== professor.university) {
    t21 = !!professor.featuredDegree && <p className={styles.affiliation}>{professor.featuredDegree}:{" "}<UniversityBadge name={professor.university} /></p>;
    $[47] = professor.featuredDegree;
    $[48] = professor.university;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== professor.university || $[51] !== showTeaches) {
    t22 = showTeaches && <p className={styles.affiliation}>also teaches at:{" "}<UniversityBadge name={professor.university} /></p>;
    $[50] = professor.university;
    $[51] = showTeaches;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== professor.taught || $[54] !== professor.university) {
    t23 = !!professor.taught && <p className={styles.affiliation}>formerly taught at:{" "}<UniversityBadge name={professor.university} /></p>;
    $[53] = professor.taught;
    $[54] = professor.university;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== professor.degrees) {
    t24 = professor.degrees.map(_temp);
    $[56] = professor.degrees;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t24) {
    t25 = <div className={styles.degrees}>{t24}</div>;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <ThinArrowDownLongIcon />;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== scrollTo) {
    t27 = () => scrollTo(bioRef.current);
    $[61] = scrollTo;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== professor.firstName || $[64] !== t27) {
    t28 = <Button className={styles["scroll-button"]} iconRight={t26} onClick={t27} size="sm" tone="neutral" variant="text">Meet {professor.firstName}</Button>;
    $[63] = professor.firstName;
    $[64] = t27;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== t19 || $[67] !== t20 || $[68] !== t21 || $[69] !== t22 || $[70] !== t23 || $[71] !== t25 || $[72] !== t28) {
    t29 = <div className={styles["hero-copy-cell"]}><PageHero.Content placement="split">{t19}{t20}{t21}{t22}{t23}{t25}{t28}</PageHero.Content></div>;
    $[66] = t19;
    $[67] = t20;
    $[68] = t21;
    $[69] = t22;
    $[70] = t23;
    $[71] = t25;
    $[72] = t28;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] !== t18 || $[75] !== t29) {
    t30 = <PageHero><PageHero.Container width="full"><div className={styles["hero-grid"]}>{t18}{t29}</div></PageHero.Container></PageHero>;
    $[74] = t18;
    $[75] = t29;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== professor.firstName) {
    t31 = <p className={styles["bio-title"]}>Meet {professor.firstName}</p>;
    $[77] = professor.firstName;
    $[78] = t31;
  } else {
    t31 = $[78];
  }
  let t32;
  if ($[79] !== professor.intro) {
    t32 = professor.intro?.map(_temp2);
    $[79] = professor.intro;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== t31 || $[82] !== t32) {
    t33 = <div>{t31}{t32}</div>;
    $[81] = t31;
    $[82] = t32;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== professor.identity || $[85] !== professor.showIdentityIcons) {
    let t35;
    if ($[87] !== professor.showIdentityIcons) {
      t35 = i => <div className={styles.interest} key={i}>{professor.showIdentityIcons !== false && <InterestIcon icon={INTERESTS[i].icon} />}{INTERESTS[i].title}</div>;
      $[87] = professor.showIdentityIcons;
      $[88] = t35;
    } else {
      t35 = $[88];
    }
    t34 = professor.identity.map(t35);
    $[84] = professor.identity;
    $[85] = professor.showIdentityIcons;
    $[86] = t34;
  } else {
    t34 = $[86];
  }
  let t35;
  if ($[89] !== t34) {
    t35 = <div className={styles.interests}>{t34}</div>;
    $[89] = t34;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] !== t33 || $[92] !== t35) {
    t36 = <Section><Container><div className={styles.bio} ref={bioRef}>{t33}{t35}</div></Container></Section>;
    $[91] = t33;
    $[92] = t35;
    $[93] = t36;
  } else {
    t36 = $[93];
  }
  let t37;
  if ($[94] !== professor.faqs) {
    t37 = professor.faqs ?? [];
    $[94] = professor.faqs;
    $[95] = t37;
  } else {
    t37 = $[95];
  }
  let t38;
  if ($[96] !== t37) {
    t38 = t37.map(_temp4);
    $[96] = t37;
    $[97] = t38;
  } else {
    t38 = $[97];
  }
  let t39;
  if ($[98] !== professor.name) {
    t39 = <>Ask {professor.name}</>;
    $[98] = professor.name;
    $[99] = t39;
  } else {
    t39 = $[99];
  }
  let t40;
  if ($[100] !== t38 || $[101] !== t39) {
    t40 = <SiteFAQSection columns="6-6" items={t38} title={t39} />;
    $[100] = t38;
    $[101] = t39;
    $[102] = t40;
  } else {
    t40 = $[102];
  }
  let t41;
  let t42;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <CTA />;
    t42 = <ApplyNow />;
    $[103] = t41;
    $[104] = t42;
  } else {
    t41 = $[103];
    t42 = $[104];
  }
  let t43;
  if ($[105] !== t30 || $[106] !== t36 || $[107] !== t40 || $[108] !== t7) {
    t43 = <div id="wrapper">{t7}{t30}{t36}{t40}{t41}{t42}</div>;
    $[105] = t30;
    $[106] = t36;
    $[107] = t40;
    $[108] = t7;
    $[109] = t43;
  } else {
    t43 = $[109];
  }
  let t44;
  if ($[110] !== site || $[111] !== t43) {
    t44 = <Layout site={site}>{t43}</Layout>;
    $[110] = site;
    $[111] = t43;
    $[112] = t44;
  } else {
    t44 = $[112];
  }
  return t44;
}
function _temp4(faq) {
  return {
    question: faq.question,
    answer: Array.isArray(faq.answers) ? faq.answers.map(_temp3) : faq.answers ?? null
  };
}
function _temp3(a, i_0) {
  return <p key={i_0}>{a}</p>;
}
function _temp2(intro, key_0) {
  return <p className={styles["bio-text"]} key={key_0}>{intro}</p>;
}
function _temp(degree, key) {
  return <p className={styles.degree} key={key}><LightGraduationCapIcon className={styles["degree-icon"]} />{degree}</p>;
}
export default Professor;